import { Component, input, signal } from '@angular/core';

/**
 * A profile or group picture that fills its (round, position: relative) avatar circle and covers the
 * initials / icon underneath. If there is no picture, or it fails to load, nothing is drawn.
 */
@Component({
  selector: 'app-chat-avatar-img',
  standalone: true,
  template: `@if (src() && failed() !== src()) { <img [src]="src()!" alt="" (error)="failed.set(src())" /> }`,
  styles: [`
    :host { position: absolute; inset: 0; display: block; overflow: hidden; border-radius: inherit; pointer-events: none; }
    img { display: block; width: 100%; height: 100%; object-fit: cover; }
  `],
})
export class AvatarImgComponent {
  readonly src = input<string | null | undefined>(null);
  protected readonly failed = signal<string | null | undefined>(null);
}

const AVATAR_SIZE = 256;

/** Crops a picked image to a centred square and shrinks it, returning a JPEG data URL ready to upload. */
export const readAvatarFile = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    if (!file.type.startsWith('image/')) return reject(new Error('Choose an image file'));
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const side = Math.min(img.naturalWidth, img.naturalHeight);
      const canvas = document.createElement('canvas');
      canvas.width = canvas.height = AVATAR_SIZE;
      const ctx = canvas.getContext('2d');
      if (!ctx) { URL.revokeObjectURL(url); return reject(new Error('Could not read the image')); }
      ctx.fillStyle = '#fff';
      ctx.fillRect(0, 0, AVATAR_SIZE, AVATAR_SIZE);
      ctx.drawImage(img, (img.naturalWidth - side) / 2, (img.naturalHeight - side) / 2, side, side, 0, 0, AVATAR_SIZE, AVATAR_SIZE);
      URL.revokeObjectURL(url);
      resolve(canvas.toDataURL('image/jpeg', 0.85));
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Could not read the image')); };
    img.src = url;
  });
